// Clipboard interop. TSV is the format Excel, Google Sheets and Numbers all put
// on the clipboard, so a plain-text round-trip is the whole interop story —
// no .xlsx parser required.

/**
 * Parse clipboard text into a grid. Handles Excel's quoting: a field containing
 * a tab, newline or quote is wrapped in double quotes with `""` escapes.
 */
export function parseTsv(text: string, delimiter = '\t'): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
      i++;
      continue;
    }
    if (ch === delimiter) {
      endField();
      i++;
      continue;
    }
    if (ch === '\r') {
      i++;
      continue;
    }
    if (ch === '\n') {
      endRow();
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  // A trailing newline should not produce a phantom row.
  if (field !== '' || row.length > 0) endRow();
  return rows;
}

export function toTsv(cells: readonly (readonly string[])[], delimiter = '\t'): string {
  return cells
    .map((row) => row.map((cell) => quoteIfNeeded(cell, delimiter)).join(delimiter))
    .join('\n');
}

function quoteIfNeeded(value: string, delimiter: string): string {
  if (value.includes(delimiter) || value.includes('\n') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** CSV export uses commas and always ends with a newline, as tools expect. */
export function toCsv(cells: readonly (readonly string[])[]): string {
  return `${toTsv(cells, ',')}\n`;
}

/** Trigger a file download from the iframe. Blob URLs work for same-document saves. */
export function downloadText(filename: string, text: BlobPart, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
